/**
 * SuccessModal Component
 *
 * Displays a success modal after ritual creation.
 * Shows confirmation message with action buttons.
 */

import { Check, Link2 } from "lucide-react"
import { useCallback, useState } from "react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader } from "@/components/ui/dialog"
import { ConnectInstagramPrompt } from "@/features/social-share/components/ConnectInstagramPrompt"
import { InstagramShareDialog } from "@/features/social-share/components/InstagramShareDialog"
import { useInstagramConnection } from "@/features/social-share/hooks/useInstagramConnection"
import { useTranslation } from "@/language"
import { getAssetUrl } from "@/utils/assets"

const socialPlatforms = [
  {
    name: "Instagram",
    icon: getAssetUrl("instagram.svg"),
    color:
      "hover:bg-gradient-to-br hover:from-[#833AB4]/20 hover:via-[#FD1D1D]/20 hover:to-[#F77737]/20 hover:border-[#E1306C]",
  },
  {
    name: "Facebook",
    icon: getAssetUrl("facebook.svg"),
    color: "hover:bg-[#1877F2]/20 hover:border-[#1877F2]",
    getUrl: (url: string, text: string) =>
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`,
  },
  {
    name: "TikTok",
    icon: getAssetUrl("ticktok.svg"),
    color: "hover:bg-[#00f2ea]/20 hover:border-[#00f2ea]",
    getUrl: (_url: string, text: string) =>
      `https://www.tiktok.com/search?q=${encodeURIComponent(text)}`,
  },
  {
    name: "Threads",
    icon: getAssetUrl("thread.svg"),
    color: "hover:bg-black/20 hover:border-black",
    getUrl: (url: string, text: string) =>
      `https://www.threads.net/intent/post?text=${encodeURIComponent(`${text} ${url}`)}`,
  },
]

function getShareBaseUrl(): string {
  const appUrl = import.meta.env.VITE_APP_URL
  if (appUrl) {
    const host = appUrl.startsWith("http") ? new URL(appUrl).origin : `https://${appUrl}`
    return host
  }
  const origin = typeof window !== "undefined" ? window.location.origin : ""
  return origin.replace(/^http:/, "https:")
}

interface SuccessModalProps {
  isOpen: boolean
  onViewRitual: () => void
  onBackToRituality?: () => void
  onShareRitual?: () => void
  hideShare?: boolean
  ritualId?: string
  ritualTitle?: string
  ritualDescription?: string
  ritualImageUrl?: string | null
  shareUrl?: string
  shareText?: string
}

export function SuccessModal({
  isOpen,
  onViewRitual,
  onBackToRituality,
  onShareRitual,
  hideShare = false,
  ritualId = "",
  ritualTitle = "",
  ritualDescription = "",
  ritualImageUrl = null,
  shareUrl = "",
  shareText = "",
}: SuccessModalProps) {
  const { t } = useTranslation("rituals")
  const [copied, setCopied] = useState(false)
  const [showPublishDialog, setShowPublishDialog] = useState(false)
  const { isConnected: isInstagramConnected } = useInstagramConnection()

  const baseUrl = getShareBaseUrl()
  const resolvedShareUrl = shareUrl.startsWith("http")
    ? shareUrl.replace(/^http:/, "https:")
    : `${baseUrl}${shareUrl}`

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(resolvedShareUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Silently fail
    }
  }

  const handleInstagramClick = useCallback(() => {
    if (isInstagramConnected) {
      setShowPublishDialog(true)
    }
    // Unconnected case is handled by the inline ConnectInstagramPrompt
  }, [isInstagramConnected])

  const handleShare = (platform: (typeof socialPlatforms)[0]) => {
    if (platform.name === "Instagram") {
      handleInstagramClick()
      return
    }
    if (platform.getUrl) {
      window.open(
        platform.getUrl(resolvedShareUrl, shareText),
        "_blank",
        "width=600,height=400,noopener,noreferrer",
      )
    }
  }

  // If the publish dialog is open, render it on top of the success modal
  if (showPublishDialog) {
    return (
      <>
        <Dialog open={isOpen}>
          <DialogContent
            showCloseButton={false}
            className="sm:max-w-lg text-center px-5 py-8.75 md:p-8"
            overlayClass="bg-[#020202] md:bg-[rgb(0,0,0,0.6)]"
          >
            <DialogHeader className="flex flex-col items-center">
              <h2 className="text-2xl font-semibold text-white uppercase mt-2">
                Your Ritual Is Live!
              </h2>
              <DialogDescription className="text-primary mt-2 text-center text-sm leading-5.5">
                <p>{t("successModal.published")}</p>
                <p>{t("successModal.discover")}</p>
              </DialogDescription>
            </DialogHeader>
          </DialogContent>
        </Dialog>
        <InstagramShareDialog
          open={showPublishDialog}
          onOpenChange={setShowPublishDialog}
          shareContext={{
            type: "ritual",
            ritualId,
            title: ritualTitle || shareText,
            description: ritualDescription || shareText || "",
            imageUrl: ritualImageUrl,
          }}
        />
      </>
    )
  }

  return (
    <Dialog open={isOpen}>
      <DialogContent
        showCloseButton={false}
        className="sm:max-w-lg text-center px-5 py-8.75 md:p-8"
        overlayClass="bg-[#020202] md:bg-[rgb(0,0,0,0.6)]"
      >
        <DialogHeader className="flex flex-col items-center">
          {/* Title */}
          <h2 className="text-2xl font-semibold text-white uppercase mt-2">
            {t("successModal.title")}
          </h2>

          {/* Description */}
          <DialogDescription className="text-primary mt-2 text-center text-sm leading-5.5">
            <p>{t("successModal.published")}</p>
            <p>{t("successModal.discover")}</p>
          </DialogDescription>
        </DialogHeader>

        {/* Action Buttons */}
        <div className="hidden md:flex flex-col gap-3 mt-6">
          {/* Primary Button - View Ritual */}
          <Button onClick={onViewRitual} className="w-full bg-white font-semibold">
            View Ritual
          </Button>

          {/* Secondary Button - Share Ritual */}
          {!hideShare && onShareRitual && (
            <Button onClick={onShareRitual} variant="outline" className="w-full">
              Share Ritual
            </Button>
          )}

          {/* Tertiary Button - Back to My Rituality */}
          {onBackToRituality && (
            <Button onClick={onBackToRituality} variant="outline" className="w-full">
              Back to My Rituality
            </Button>
          )}
        </div>

        {/* Action Buttons: mobile */}
        <div className="md:hidden flex flex-col items-center gap-3 mt-6">
          {/* Primary Button - View Ritual */}
          <Button onClick={onViewRitual} className="w-full max-w-[280px] bg-white font-semibold">
            View Ritual
          </Button>
          {!hideShare && (
            <div>
              <span className="text-sm text-[#F2F0EBE6] leading-normal block text-center mt-4 mb-3.75">
                Share to social media
              </span>
              <div className="flex flex-wrap items-center justify-center gap-3.75">
                {socialPlatforms.map(platform => (
                  <button
                    key={platform.name}
                    type="button"
                    onClick={() => handleShare(platform)}
                    className={`flex items-center justify-center rounded-full w-[36px] h-[36px] md:w-12.5 md:h-12.5 transition-all duration-200 ${platform.name === "Instagram" || platform.name === "Facebook" || platform.name === "Threads" ? "p-0" : "border border-[#F2F0EB26] "} ${platform.color}`}
                    aria-label={`Share to ${platform.name}`}
                  >
                    <img
                      src={platform.icon}
                      alt={platform.name}
                      className={`${platform.name === "Instagram" || platform.name === "Facebook" || platform.name === "Threads" ? "w-full h-full" : "w-[18px] h-[18px] md:w-5 md:h-5"}`}
                    />
                  </button>
                ))}
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className={`flex items-center justify-center rounded-full w-[36px] h-[36px] md:w-12.5 md:h-12.5 transition-all duration-200 bg-[#F2F0EB0D] border border-[#F2F0EB26] hover:bg-brand-olive/20 hover:border-brand-olive ${copied ? "bg-brand-olive/20 border-brand-olive" : ""}`}
                  aria-label={t("successModal.copyLink")}
                >
                  {copied ? (
                    <Check className="w-[18px] h-[18px] md:w-5 md:h-5 text-brand-olive" />
                  ) : (
                    <Link2 className="w-[18px] h-[18px] md:w-5 md:h-5 text-[#F2F0EBE6]" />
                  )}
                </button>
              </div>
              {/* Connect prompt when unconnected user clicks Instagram */}
              {!isInstagramConnected && (
                <div className="mt-3">
                  <ConnectInstagramPrompt />
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
